import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import ComingSoonPage from "../../components/ComingSoonPage";
import { useGooglePlacesAutocomplete } from "../../hooks/useGooglePlacesAutocomplete";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
const MOBILE_ITEMS_PER_PAGE = 30;

const ShopListings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [categories, setCategories] = useState([]);
  const [comingSoon, setComingSoon] = useState(null); // { countryName, countryCode, message }
  const [searchFallbackNotice, setSearchFallbackNotice] = useState("");

  // Filters from URL
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("businessType") || "",
  );
  const [nameQuery] = useState(
    (searchParams.get("q") || searchParams.get("search") || "").trim(),
  );
  const [sortBy, setSortBy] = useState("name");
  const [sortTouched, setSortTouched] = useState(false);
  const [radiusOptions, setRadiusOptions] = useState([5, 10, 25, 50]);
  const [radiusMiles, setRadiusMiles] = useState("10");
  /**
   * WHAT THE RADIUS IS HIDING (2026-10-23). `{count, nearestMiles}` when the current radius returns nothing but
   * a wider search would reach something. Reported as "shops don't show unless I pick 50 mi" — the list was
   * right and the page explained nothing; this is what it now says instead of a bare empty state.
   */
  const [outOfRange, setOutOfRange] = useState(null);
  const [searchSettingsLoaded, setSearchSettingsLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = viewportWidth <= 768;
  const isTablet = viewportWidth > 768 && viewportWidth <= 1180;

  // Proximity search via Google Places Autocomplete
  const {
    inputValue: locationInput,
    suggestions: locationSuggestions,
    selectedCoords: userCoords,
    locationSource,
    geolocationStatus,
    showSuggestions,
    handleInputChange: handleLocationInputChange,
    selectSuggestion: handleSuggestionSelect,
    clearLocation,
    setShowSuggestions,
  } = useGooglePlacesAutocomplete();

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchPublicSearchSettings();
  }, []);

  const normalizeRadiusOptions = (rawOptions) => {
    if (!Array.isArray(rawOptions)) {
      return [5, 10, 25, 50];
    }

    const normalized = rawOptions
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value >= 1 && value <= 200);

    const uniqueSorted = [...new Set(normalized)].sort((a, b) => a - b);
    return uniqueSorted.length > 0 ? uniqueSorted : [5, 10, 25, 50];
  };

  const fetchPublicSearchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/settings/public`);
      const homepageSettings = response.data?.settings?.homepageSettings || {};
      const configuredOptions = normalizeRadiusOptions(
        homepageSettings.publicSearchRadiusOptions,
      );
      const configuredDefault = Number(
        homepageSettings.publicSearchDefaultRadiusMiles,
      );
      const fallbackDefault = configuredOptions.includes(10)
        ? 10
        : configuredOptions[0];
      const nextDefault = configuredOptions.includes(configuredDefault)
        ? configuredDefault
        : fallbackDefault;

      setRadiusOptions(configuredOptions);
      setRadiusMiles(String(nextDefault));
    } catch (err) {
      // Keep defaults when public settings are unavailable.
    } finally {
      setSearchSettingsLoaded(true);
    }
  };

  useEffect(() => {
    if (geolocationStatus === "requesting" || !searchSettingsLoaded) return;
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCategory,
    sortBy,
    sortTouched,
    radiusMiles,
    userCoords,
    geolocationStatus,
    searchSettingsLoaded,
  ]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/shops/business-types/public`,
      );
      setCategories(response.data.categories || []);
    } catch (err) {
    }
  };

  const fetchShops = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append("service", selectedCategory);
      if (nameQuery) params.append("q", nameQuery);
      if (userCoords) {
        params.append("lat", userCoords.lat);
        params.append("lng", userCoords.lng);
        params.append("radius", radiusMiles);
        if (locationSource === "manual") {
          const parts = userCoords.label?.split(",").map((s) => s.trim());
          const searchCity = parts?.[0];
          const stateFromLabel = parts?.[1]?.split(" ")[0];
          if (searchCity) params.append("searchCity", searchCity);
          if (stateFromLabel) params.append("state", stateFromLabel);
        }
        if (sortTouched) {
          params.append("sort", sortBy);
          params.append("sortManual", "1");
        }
      } else {
        params.append("sort", sortBy);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/shops/public?${params.toString()}`,
      );

      // Check if user is from a country where OGESTYLE is not yet available
      if (response.data.comingSoon) {
        setSearchFallbackNotice("");
        setComingSoon({
          countryName: response.data.countryName,
          countryCode: response.data.userCountry,
          message: response.data.message,
        });
        setLoading(false);
        return; // Don't show shops
      }

      setShops(response.data.shops || []);
      setSearchFallbackNotice(response.data.searchFallbackNotice || "");
      setLoading(false);

      /**
       * WHY AN EMPTY LIST HAS TO EXPLAIN ITSELF (2026-10-23).
       *
       * Reported as "shops are not showing unless I change the distance to 50 mi". The list was correct: the
       * shop is 37 miles from the chosen location and the radius was 25, so it was excluded — but the page said
       * only "No businesses found", which reads as a broken search and hides a one-second fix. When the filtered
       * request comes back empty, ask once more at the widest radius purely to find out whether anything exists
       * and how far away it is, then say so. Read-only, one extra request, and only in the empty case.
       */
      if ((response.data.shops || []).length === 0 && userCoords) {
        const widest = new URLSearchParams(params.toString());
        widest.set("radius", "100");
        try {
          const probe = await axios.get(
            `${API_BASE_URL}/api/shops/public?${widest.toString()}`,
          );
          const near = (probe.data.shops || [])
            .map((shop) => Number(shop._distanceMiles))
            .filter((miles) => Number.isFinite(miles))
            .sort((a, b) => a - b);
          setOutOfRange(
            near.length > 0
              ? { count: near.length, nearestMiles: Math.round(near[0]) }
              : null,
          );
        } catch {
          setOutOfRange(null);
        }
      } else {
        setOutOfRange(null);
      }
    } catch (err) {
      setSearchFallbackNotice("");
      setLoading(false);
    }
  };

  const filteredShops = shops;
  const totalPages = isMobile
    ? Math.max(1, Math.ceil(filteredShops.length / MOBILE_ITEMS_PER_PAGE))
    : 1;
  const paginatedShops = isMobile
    ? filteredShops.slice(
        (currentPage - 1) * MOBILE_ITEMS_PER_PAGE,
        currentPage * MOBILE_ITEMS_PER_PAGE,
      )
    : filteredShops;

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, sortBy, sortTouched, radiusMiles, userCoords, nameQuery]);

  const usingBrowserLocation = locationSource === "browser";
  const usingManualLocation = locationSource === "manual";
  const displayedSortValue = userCoords && !sortTouched ? "distance" : sortBy;

  const getSortLabel = () => {
    switch (sortBy) {
      case "distance":
        return "Nearest";
      case "rating":
        return "Rating";
      case "professionalCount":
        return "Most Pros";
      case "city":
        return "City";
      case "name":
      default:
        return "A-Z";
    }
  };

  const handleSortChange = (e) => {
    if (e.target.value === "distance") {
      setSortTouched(false);
      return;
    }
    setSortBy(e.target.value);
    setSortTouched(true);
  };

  const handleLocationSelect = (prediction) => {
    setSortTouched(false);
    handleSuggestionSelect(prediction);
  };

  const handleClearLocation = () => {
    setSortTouched(false);
    clearLocation();
  };

  const handleCategoryClick = (categoryValue) => {
    setSelectedCategory(
      categoryValue === selectedCategory ? "" : categoryValue,
    );
  };

  const handleShopClick = (shop) => {
    const businessSlug = shop.businessId?.slug;
    const locationSlug = shop.slug;

    if (!businessSlug || !locationSlug) {
      alert(
        `Navigation error: Shop "${shop.name}" is missing required slugs. Please contact support.`,
      );
      return;
    }

    navigate(`/business/${businessSlug}/${locationSlug}`);
  };

  return (
    <>
      <CustomerHeader />

      {/* Country Availability Check */}
      {comingSoon ? (
        <ComingSoonPage
          countryName={comingSoon.countryName}
          countryCode={comingSoon.countryCode}
          source="shop_listings"
        />
      ) : (
        <div
          style={{
            minHeight: "100vh",
            background: "#f9fafb",
            paddingTop: "calc(var(--demo-banner-height, 0px) + 50px)",
          }}
        >
          {/* Header Section */}
          <div
            style={{
              background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
              color: "white",
              padding: isMobile
                ? "40px 16px"
                : isTablet
                  ? "48px 28px"
                  : "60px 48px",
              textAlign: "center",
            }}
          >
            <h1
              style={{
                fontSize: "14px",
                fontWeight: "700",
                marginBottom: "12px",
              }}
            >
              🏪 Discover Beauty & Wellness Businesses
            </h1>
            <p
              style={{
                fontSize: "12px",
                color: "rgba(255,255,255,0.9)",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Find top-rated salons, spas, barbershops and wellness centers near
              you
            </p>
          </div>

          {/* Search and Filters Container */}
          <div
            style={{
              maxWidth: isTablet ? "980px" : "1200px",
              margin: "0 auto",
              padding: isMobile
                ? "24px 16px"
                : isTablet
                  ? "28px 24px"
                  : "32px 48px",
            }}
          >
            {/* Location Search (Proximity) */}
            {/* Search Row: location input + sort */}
            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                gap: "12px",
                alignItems: "flex-start",
                flexDirection: isMobile ? "column" : "row",
                flexWrap: isTablet ? "wrap" : "nowrap",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
            <div
              style={{
                flex: 1,
                width: "100%",
                position: "relative",
                gridColumn: isMobile ? "1 / -1" : "auto",
                gridRow: isMobile ? "1" : "auto",
                minWidth: 0,
              }}
            >
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", pointerEvents: "none" }}>
                  📍
                </span>
                <input
                  type="text"
                  placeholder="Search near a city or address (e.g. Chicago, IL)..."
                  value={locationInput}
                  onChange={(e) => handleLocationInputChange(e.target.value)}
                  onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  style={{
                    width: "100%",
                    padding: "14px 44px",
                    fontSize: "12px",
                    border: userCoords ? "1.5px solid #2d6da3" : "1px solid #d1d5db",
                    borderRadius: "10px",
                    outline: "none",
                    background: userCoords ? "#eff6ff" : "white",
                    boxSizing: "border-box",
                  }}
                />
                {locationInput && (
                  <button
                    onClick={handleClearLocation}
                    style={{
                      position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#9ca3af",
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {userCoords && (
                <p style={{ fontSize: "11px", color: "#2d6da3", marginTop: "6px" }}>
                  {usingBrowserLocation ? (
                    <>✓ Showing businesses closest to <strong>your location</strong></>
                  ) : (
                    <>✓ Showing businesses closest to <strong>{userCoords.label}</strong></>
                  )}
                </p>
              )}
              {userCoords && (
                <p style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}>
                  {sortTouched
                    ? `Sorted by ${getSortLabel()} (manual override)`
                    : usingManualLocation
                      ? `Sorted by distance (${radiusMiles} mi): searched city first, then nearby areas`
                      : `Sorted by distance (${radiusMiles} mi): nearest businesses first`}
                </p>
              )}
              {usingManualLocation && userCoords && searchFallbackNotice && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "#92400e",
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: "8px",
                    padding: "8px 10px",
                    marginTop: "8px",
                  }}
                >
                  {searchFallbackNotice}
                </p>
              )}
              {!userCoords && (geolocationStatus === "denied" || geolocationStatus === "error" || geolocationStatus === "unavailable") && (
                <p style={{ fontSize: "11px", color: "#6b7280", marginTop: "6px" }}>
                  Location access is off, so results are using the default listing order. Search by city to see nearby businesses first.
                </p>
              )}
              {showSuggestions && locationSuggestions.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                  background: "white", border: "1px solid #e5e7eb", borderRadius: "10px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden", marginTop: "4px",
                }}>
                  {locationSuggestions.map((pred) => (
                    <div
                      key={pred.place_id}
                      onMouseDown={() => handleLocationSelect(pred)}
                      style={{
                        padding: "12px 16px", cursor: "pointer", fontSize: "12px", color: "#374151",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                    >
                      📍 {pred.description}
                    </div>
                  ))}
                </div>
              )}
            </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  width: isMobile ? "100%" : "auto",
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                <select
                  value={displayedSortValue}
                  onChange={handleSortChange}
                  style={{
                    padding: "14px 16px",
                    fontSize: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "10px",
                    outline: "none",
                    background: "white",
                    cursor: "pointer",
                    width: isMobile ? "calc((100% - 12px) / 2)" : isTablet ? "220px" : "180px",
                    flex: isMobile ? "1 1 0" : "0 0 auto",
                    minWidth: 0,
                    boxSizing: "border-box",
                  }}
                >
                  <option value="distance" disabled={!userCoords}>Sort: Nearest</option>
                  <option value="name">Sort: A-Z</option>
                  <option value="rating">Sort: Rating</option>
                  <option value="professionalCount">Sort: Most Pros</option>
                </select>

                <select
                  value={radiusMiles}
                  onChange={(e) => setRadiusMiles(e.target.value)}
                  style={{
                    padding: "14px 16px",
                    fontSize: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "10px",
                    outline: "none",
                    background: "white",
                    cursor: "pointer",
                    width: isMobile ? "calc((100% - 12px) / 2)" : isTablet ? "180px" : "140px",
                    flex: isMobile ? "1 1 0" : "0 0 auto",
                    minWidth: 0,
                    boxSizing: "border-box",
                  }}
                >
                  {radiusOptions.map((radius) => (
                    <option key={radius} value={String(radius)}>
                      Distance: {radius} mi
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category Filter Pills */}
            {false && categories.length > 0 && (
              <div style={{ marginBottom: "24px" }}>
                <h3
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#6b7280",
                  }}
                >
                  Filter by Category:
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <button
                    onClick={() => setSelectedCategory("")}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "20px",
                      border:
                        selectedCategory === ""
                          ? "2px solid #2d6da3"
                          : "1px solid #d1d5db",
                      background: selectedCategory === "" ? "#eff6ff" : "white",
                      color: selectedCategory === "" ? "#2d6da3" : "#6b7280",
                      fontSize: "11px",
                      fontWeight: selectedCategory === "" ? "600" : "500",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    All Categories
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => handleCategoryClick(cat.value)}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "20px",
                        border:
                          selectedCategory === cat.value
                            ? "2px solid #2d6da3"
                            : "1px solid #d1d5db",
                        background:
                          selectedCategory === cat.value ? "#eff6ff" : "white",
                        color:
                          selectedCategory === cat.value
                            ? "#2d6da3"
                            : "#6b7280",
                        fontSize: "11px",
                        fontWeight:
                          selectedCategory === cat.value ? "600" : "500",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* City dropdown removed — search + sort row above handles this */}

            {/* Results Count */}
            <p
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "20px",
              }}
            >
              {loading
                ? "Loading..."
                : `${filteredShops.length} business${filteredShops.length !== 1 ? "es" : ""} found`}
            </p>

            {/* Shops Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(3, 1fr)"
                  : isTablet
                    ? "repeat(auto-fit, minmax(170px, 1fr))"
                    : "repeat(auto-fill, 180px)",
                gap: isMobile ? "0px 2px" : isTablet ? "8px 10px" : "6px",
                alignItems: "start",
              }}
            >
              {paginatedShops.map((shop) => (
                <div
                  key={shop._id}
                  onClick={() => handleShopClick(shop)}
                  style={{
                    background: "transparent",
                    borderRadius: "12px",
                    overflow: "visible",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    width: isMobile ? "100%" : isTablet ? "100%" : "180px",
                    // minHeight removed to allow natural height
                    height: "auto",
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      height: "126px",
                      width: "100%",
                      backgroundColor: "transparent",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      position: "relative",
                      paddingBottom: "2px",
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? "88px" : isTablet ? "96px" : "112px",
                        height: isMobile ? "88px" : isTablet ? "96px" : "112px",
                        borderRadius: "999px",
                        // U3: brand image (business logo) only. No shop-image
                        // fallback - when no logo is uploaded show the first
                        // letter of the business name on the brand gradient.
                        background: shop.businessId?.logo?.url
                          ? `url("${shop.businessId.logo.url}") center/cover no-repeat`
                          : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 16px rgba(0,0,0,0.14)",
                        color: "white",
                        fontSize: "30px",
                        fontWeight: "700",
                      }}
                    >
                      {!shop.businessId?.logo?.url &&
                        (shop.name?.[0] || "✨")}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "3px 8px 2px 8px",
                      textAlign: "center",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: isMobile ? "12px" : "15px",
                        fontWeight: "600",
                        color: "#111827",
                        marginTop: "0",
                        marginBottom: "0",
                        lineHeight: "1",
                        overflow: "visible",
                        textOverflow: "clip",
                        whiteSpace: "normal",
                      }}
                    >
                      {shop.name}
                    </h3>

                    {(shop.city || shop.state) && (
                      <p
                        style={{
                          fontSize: isMobile ? "10px" : "12px",
                          color: "#6b7280",
                          marginTop: "0",
                          marginBottom: "2px",
                          lineHeight: "1.2",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {[shop.city, shop.state].filter(Boolean).join(", ")}
                      </p>
                    )}

                    {shop.professionalCount > 0 && (
                      <p
                        style={{
                          fontSize: isMobile ? "10px" : "12px",
                          color: "#1a659e",
                          marginTop: "0",
                          marginBottom: "2px",
                          fontWeight: "500",
                          lineHeight: "1.2",
                        }}
                      >
                        👥 {shop.professionalCount} {shop.professionalCount === 1 ? "Pro" : "Pros"}
                      </p>
                    )}

                    {shop.rating && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                          lineHeight: "0.95",
                        }}
                      >
                        <span
                          style={{
                            color: "#f59e0b",
                            fontSize: isMobile ? "10px" : "13px",
                          }}
                        >
                          ★★★★★
                        </span>
                        <span
                          style={{
                            fontSize: isMobile ? "10px" : "13px",
                            color: "#6b7280",
                          }}
                        >
                          ({shop.rating.toFixed(1)})
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {isMobile && totalPages > 1 && (
              <div
                style={{
                  marginTop: "14px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    background: currentPage === 1 ? "#f3f4f6" : "white",
                    color: currentPage === 1 ? "#9ca3af" : "#111827",
                    fontSize: "11px",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Prev
                </button>
                <span style={{ fontSize: "11px", color: "#6b7280" }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  style={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    background:
                      currentPage === totalPages ? "#f3f4f6" : "white",
                    color:
                      currentPage === totalPages ? "#9ca3af" : "#111827",
                    fontSize: "11px",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {/* Empty State */}
            {!loading && filteredShops.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "#6b7280",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔍</div>
                <h3
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#111827",
                  }}
                >
                  No businesses found
                </h3>
                {/* SHOP EXISTS, RADIUS TOO SMALL (2026-10-23). Saying only "nothing found" when a shop sits
                    37 miles away at a 25-mile radius is the difference between "the search is broken" and
                    "press this". The sentence names the distance, and the button sets the narrowest configured
                    radius that would actually reach it. */}
                {outOfRange && (
                  <div
                    style={{
                      maxWidth: "420px",
                      margin: "0 auto 12px auto",
                      padding: "12px 14px",
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "8px",
                      color: "#1e40af",
                      fontSize: "12px",
                      lineHeight: 1.6,
                    }}
                  >
                    <strong>
                      {outOfRange.count === 1
                        ? "1 business is"
                        : `${outOfRange.count} businesses are`}{" "}
                      {outOfRange.nearestMiles} mi away
                    </strong>{" "}
                    — outside your {radiusMiles} mi search.
                    <div style={{ marginTop: "8px" }}>
                      <button
                        type="button"
                        onClick={() => {
                          const reach = radiusOptions
                            .map(Number)
                            .filter((r) => r >= outOfRange.nearestMiles)
                            .sort((a, b) => a - b)[0];
                          setSortTouched(false);
                          setRadiusMiles(String(reach || 100));
                        }}
                        style={{
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: "none",
                          background: "#2d6da3",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Search {outOfRange.nearestMiles} mi instead
                      </button>
                    </div>
                  </div>
                )}
                <p style={{ fontSize: "12px" }}>
                  Try adjusting your search or filters
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      <CustomerFooter />
    </>
  );
};

export default ShopListings;
